export const REQUEST_TYPES = {
  HIRE: 'hire',
  CAMPAIGN: 'campaign',
};

export const FLOW_TYPES = {
  SEQUENTIAL: 'sequential',
  PARALLEL: 'parallel',
};

export const REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  WITHDRAWN: 'withdrawn',
};

export const APPROVER_STEP_STATUS = {
  APPROVED: 'approved',
  PENDING: 'pending',
  UPCOMING: 'upcoming',
  AWAITING: 'awaiting',
};

export const TYPE_FILTER_OPTIONS = [
  { id: 'all', label: 'All types', iconKey: 'shield' },
  { id: REQUEST_TYPES.HIRE, label: 'Hire', iconKey: 'briefcase' },
  { id: REQUEST_TYPES.CAMPAIGN, label: 'Campaign', iconKey: 'megaphone' },
];

export const STATUS_TAB_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'awaiting_me', label: 'Awaiting me' },
  { id: 'my_requests', label: 'My requests' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

export const HIRE_WIZARD_STEPS = [
  { id: 1, key: 'role_details', label: 'Role details' },
  { id: 2, key: 'approval_chain', label: 'Approval chain' },
  { id: 3, key: 'review', label: 'Review' },
];

export const CAMPAIGN_WIZARD_STEPS = [
  { id: 1, key: 'campaign_details', label: 'Campaign details' },
  { id: 2, key: 'approval_chain', label: 'Approval chain' },
  { id: 3, key: 'review', label: 'Review' },
];

export const REJECTION_ACTION_OPTIONS = [
  {
    id: 'cancel',
    label: 'Cancel the campaign',
    description: 'Stop the campaign request entirely.',
  },
  {
    id: 'resubmit_lower',
    label: 'Re-submit with a lower budget',
    description: 'Automatically prompt for a revised budget.',
  },
  {
    id: 'decide_later',
    label: 'Let me decide at the time',
    description: 'Choose what to do when the request is rejected.',
  },
];

export const REJECTION_RADIO_OPTIONS = REJECTION_ACTION_OPTIONS.map((o) => ({
  value: o.id,
  label: o.label,
}));

export const APPROVAL_FLOW_OPTIONS = [
  {
    value: FLOW_TYPES.SEQUENTIAL,
    label: 'Sequential',
    description: 'Approvers notified one at a time, in order.',
  },
  {
    value: FLOW_TYPES.PARALLEL,
    label: 'Parallel',
    description: 'All approvers notified at once. All must approve.',
  },
];

export const CHAIN_FLOW_OPTIONS = [
  {
    value: FLOW_TYPES.SEQUENTIAL,
    label: 'Sequential',
    description: 'One after the other.',
  },
  {
    value: FLOW_TYPES.PARALLEL,
    label: 'Parallel',
    description: 'All at once.',
  },
];

export const CHAIN_TYPE_OPTIONS = [
  { value: REQUEST_TYPES.HIRE, label: 'Hire approval' },
  { value: REQUEST_TYPES.CAMPAIGN, label: 'Campaign approval' },
];

export const CHAIN_MODAL_TABS = [
  { id: 'all', label: 'All' },
  { id: REQUEST_TYPES.HIRE, label: 'Hire', iconKey: 'briefcase' },
  { id: REQUEST_TYPES.CAMPAIGN, label: 'Campaign', iconKey: 'megaphone' },
];

export const STATUS_BADGE_CONFIG = {
  [REQUEST_STATUS.PENDING]: { label: 'Pending Approval', variant: 'pending' },
  [REQUEST_STATUS.APPROVED]: { label: 'Approved', variant: 'approved' },
  [REQUEST_STATUS.REJECTED]: { label: 'Rejected', variant: 'rejected' },
  [REQUEST_STATUS.WITHDRAWN]: { label: 'Withdrawn', variant: 'withdrawn' },
};

export const TYPE_BADGE_CONFIG = {
  [REQUEST_TYPES.HIRE]: { label: 'Hire', iconKey: 'briefcase' },
  [REQUEST_TYPES.CAMPAIGN]: { label: 'Campaign', iconKey: 'megaphone' },
};

export const EMPTY_STATE_COPY = {
  all: {
    title: 'No requests yet',
    description: 'Create a new approval request to get started.',
    actionLabel: 'New request',
  },
  awaiting_me: {
    title: "You're all caught up",
    description: 'No requests are waiting on your approval right now.',
  },
  in_progress: {
    title: 'No requests in progress',
    description: 'You have no open requests awaiting approval right now.',
  },
  needs_amendment: {
    title: 'Nothing needs amendment',
    description: 'Rejected requests that need changes will appear here.',
  },
  approved: {
    title: 'No approved requests',
    description: 'Approved requests will appear in this tab.',
  },
  rejected: {
    title: 'No rejected requests',
    description: 'Rejected requests will appear in this tab.',
  },
  default: {
    title: 'No requests found',
    description: 'Try adjusting your filters to see more results.',
  },
};

export const ERROR_COPY = {
  loadFailed: {
    title: 'Unable to load requests',
    message: 'We could not load approval requests. Check your connection and try again.',
  },
  detailLoadFailed: {
    title: 'Unable to load request details',
    message: 'We could not load this request. Check your connection and try again.',
  },
  referenceDataMissing: {
    title: 'Form data unavailable',
    message: 'Reference data has not loaded yet. Close this dialog and try again.',
  },
};

export const REQUEST_ACTION_CONFIRM = {
  approve: {
    title: 'Approve this request?',
    confirmLabel: 'Yes, Approve',
    getDescriptionLines: (title) => [
      title
        ? `You are about to approve "${title}".`
        : 'You are about to approve this recruitment request.',
      'The requester and other approvers will be notified.',
    ],
  },
  reject: {
    title: 'Reject this request?',
    confirmLabel: 'Yes, Reject',
    getDescriptionLines: (title) => [
      title
        ? `You are about to reject "${title}".`
        : 'You are about to reject this recruitment request.',
      'The approval chain will stop and the requester will be notified.',
    ],
  },
  withdraw: {
    title: 'Withdraw this request?',
    confirmLabel: 'Yes, Withdraw',
    getDescriptionLines: (title) => [
      title
        ? `You are about to withdraw "${title}".`
        : 'You are about to withdraw this recruitment request.',
      'This can only be done before any approver has acted. This action cannot be undone.',
    ],
  },
  resubmit: {
    title: 'Resubmit this request?',
    confirmLabel: 'Yes, Resubmit',
    getDescriptionLines: (title) => [
      title
        ? `You are about to resubmit "${title}" as-is.`
        : 'You are about to resubmit this request as-is.',
      'The request returns to pending. Only approvers who rejected will be asked to review again.',
    ],
  },
  amend_resubmit: {
    title: 'Resubmit with amendments?',
    confirmLabel: 'Yes, Resubmit',
    getDescriptionLines: (title) => [
      title
        ? `You are about to resubmit "${title}" with your amended details.`
        : 'You are about to resubmit this request with your amended details.',
      'All approvers will be reset and must review the updated request from the start.',
    ],
  },
  resend_notification: {
    title: 'Resend notification?',
    confirmLabel: 'Yes, Resend',
    getDescriptionLines: (title) => [
      title
        ? `Send a reminder about "${title}" to approvers who are still pending.`
        : 'Send a reminder to approvers who are still pending.',
      'They will be notified by email and in-app.',
    ],
  },
};

export const APPROVED_HIRE_ACTION_COPY = {
  title: 'Approved — ready to advertise',
  helper:
    'Approved department, location and salary band will be locked in the job wizard.',
  buttonLabel: 'Create job from request',
};

export const APPROVED_CAMPAIGN_ACTION_COPY = {
  title: 'Approved — ready to launch',
  helper: 'Campaign settings locked at approval.',
  buttonLabel: 'Launch campaign',
};

export const JOB_CREATED_FROM_REQUEST_COPY = {
  titleBadge: 'Job created',
  bannerMessage: 'Job created from this request.',
  buttonLabel: 'View job',
  copywritingRequestedTag: 'Copywriting requested',
};

export const CAMPAIGN_LAUNCHED_FROM_REQUEST_COPY = {
  titleBadge: 'Launched',
  bannerMessage: 'Campaign launched from this request.',
  buttonLabel: 'View job posting',
};

export const RESEND_NOTIFICATION_COPY = {
  tooltip: 'Resend notification to awaiting approver(s)',
  footerLabel: 'Resend notification',
  footerHint: 'to pending approvers',
  success: 'Notification resent to awaiting approver(s).',
  error: 'Could not resend notification. Please try again.',
};

export const TOAST_MESSAGES = {
  submitSuccess: 'Request submitted for approval.',
  submitError: 'Could not submit request. Please try again.',
  approveSuccess: 'Request approved.',
  approveError: 'Could not approve request. Please try again.',
  rejectSuccess: 'Request rejected.',
  rejectError: 'Could not reject request. Please try again.',
  resubmitSuccess: 'Request resubmitted for approval.',
  resubmitError: 'Could not resubmit request. Please try again.',
  withdrawSuccess: 'Request withdrawn successfully.',
  withdrawError: 'Could not withdraw request. Please try again.',
  refreshError: 'Could not refresh the list. Please try again.',
  resendNotificationSuccess: RESEND_NOTIFICATION_COPY.success,
  resendNotificationError: RESEND_NOTIFICATION_COPY.error,
  chainCreateSuccess: 'Approval chain created.',
  chainCreateError: 'Could not create approval chain. Please try again.',
  chainUpdateSuccess: 'Approval chain updated.',
  chainUpdateError: 'Could not update approval chain. Please try again.',
  chainDeleteSuccess: 'Approval chain deleted.',
  chainDeleteError: 'Could not delete approval chain. Please try again.',
};
